import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

/**
 * Les champs qu'on remplit rarement, repliés — jamais supprimés.
 *
 * Un exploitant qui enregistre un camion se voyait demander un IMEI et un
 * secret partagé HTTP. Ces champs sont nécessaires pour qu'un boîtier remonte
 * des positions : les retirer casserait le suivi. Mais les poser au même rang
 * que la plaque et la citerne fait d'un geste de trente secondes un formulaire
 * de quatorze champs.
 *
 * Deux règles qui font la différence entre un repli utile et un repli qui
 * cache :
 *
 * Le dépliant **dit combien** de champs il contient. « Plus de détails » seul
 * laisse croire à deux ou trois lignes décoratives ; « 7 champs » annonce ce
 * qu'on trouve derrière.
 *
 * Il **s'ouvre de lui-même** si l'un des champs porte déjà une valeur. Une
 * modification ne doit jamais faire disparaître une donnée saisie : quelqu'un
 * qui a renseigné un IMEI doit le revoir en rouvrant la fiche, sans avoir à
 * deviner qu'il est là.
 */
export const ChampsAvances = ({
  titre = 'Plus de détails',
  /** Ce qu'on perd à ne pas les remplir. Une phrase, pas un paragraphe. */
  consequence,
  /** Les valeurs concernées : si l'une est renseignée, le bloc s'ouvre. */
  valeurs = [],
  nombre,
  children,
  className,
}) => {
  const dejaRempli = useMemo(
    () =>
      valeurs.some(
        (valeur) =>
          valeur !== '' &&
          valeur !== null &&
          valeur !== undefined &&
          valeur !== false,
      ),
    [valeurs],
  );

  const [ouvert, setOuvert] = useState(dejaRempli);
  const compte = nombre ?? valeurs.length;

  return (
    <Collapsible
      open={ouvert}
      onOpenChange={setOuvert}
      className={cn('rounded-xl border border-border/60', className)}
    >
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2.5 text-left">
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            ouvert && 'rotate-180',
          )}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-foreground">
            {titre}
            {compte > 0 ? (
              <span className="ml-1.5 font-normal text-muted-foreground">
                · {compte} champ{compte > 1 ? 's' : ''}
              </span>
            ) : null}
          </span>
          {consequence && !ouvert ? (
            <span className="block truncate text-xs text-muted-foreground">
              {consequence}
            </span>
          ) : null}
        </span>
        {dejaRempli && !ouvert ? (
          <span className="shrink-0 text-xs font-medium text-primary">
            renseigné
          </span>
        ) : null}
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-3 border-t border-border/60 px-3 py-3">
        {consequence ? (
          <p className="text-xs text-muted-foreground">{consequence}</p>
        ) : null}
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ChampsAvances;
